import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  ChevronDown, 
  ChevronUp,
  Box,
  Filter,
  Gauge,
  Droplets,
  Activity,
  RotateCw,
  AlertTriangle,
  Thermometer,
  Timer,
  Waves,
  Zap
} from 'lucide-react';
import { ProcessFlowDiagram } from './ProcessFlowDiagram';
import { AssetGauge } from './AssetGauge';
import { Asset } from '../types';

export const RawWaterStage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    const initialAssets: Asset[] = [
      {
        id: 'intake1',
        name: 'Raw Water Intake',
        type: 'Intake Structure',
        description: 'Primary water intake structure with debris screen',
        manufacturer: 'Custom Built',
        model: 'RS-2000',
        installationDate: '2023-04-01',
        maintenanceSchedule: 'Semi-annual',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 260,
            unit: 'm³/h',
            normalRange: { min: 200, max: 300 },
            criticalRange: { min: 150, max: 350 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 260 + Math.random() * 20 - 10
            }))
          },
          {
            name: 'Water Level',
            value: 4.2,
            unit: 'm',
            normalRange: { min: 3.5, max: 5.0 },
            criticalRange: { min: 3.0, max: 5.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 4.2 + Math.random() * 0.3 - 0.15
            }))
          },
          {
            name: 'Turbidity',
            value: 12,
            unit: 'NTU',
            normalRange: { min: 0, max: 20 },
            criticalRange: { min: 0, max: 30 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 12 + Math.random() * 4 - 2
            }))
          }
        ]
      },
      {
        id: 'screen1',
        name: 'Coarse Screen',
        type: 'Mechanical Bar Screen',
        description: 'Primary debris removal system with automated cleaning',
        manufacturer: 'Huber Technology',
        model: 'STEPSCREEN® L',
        installationDate: '2023-05-20',
        maintenanceSchedule: 'Monthly',
        status: 'warning',
        metrics: [
          {
            name: 'Flow Rate',
            value: 255,
            unit: 'm³/h',
            normalRange: { min: 200, max: 300 },
            criticalRange: { min: 150, max: 350 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 255 + Math.random() * 20 - 10
            }))
          },
          {
            name: 'Debris Level',
            value: 75,
            unit: '%',
            normalRange: { min: 0, max: 60 },
            criticalRange: { min: 0, max: 80 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 75 + Math.random() * 10 - 5
            }))
          },
          {
            name: 'Motor Current',
            value: 12.5,
            unit: 'A',
            normalRange: { min: 10, max: 15 },
            criticalRange: { min: 8, max: 17 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 12.5 + Math.random() * 1 - 0.5
            }))
          },
          {
            name: 'Operating Time',
            value: 12.5,
            unit: 'h/day',
            normalRange: { min: 8, max: 16 },
            criticalRange: { min: 6, max: 18 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 12.5 + Math.random() * 2 - 1
            }))
          }
        ]
      },
      {
        id: 'pump1',
        name: 'Raw Water Pump 1',
        type: 'Submersible Pump',
        description: 'Primary submersible pump for raw water intake',
        manufacturer: 'Grundfos',
        model: 'SP 215-5',
        installationDate: '2023-06-15',
        maintenanceSchedule: 'Quarterly',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 250,
            unit: 'm³/h',
            normalRange: { min: 200, max: 300 },
            criticalRange: { min: 150, max: 350 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 250 + Math.random() * 20 - 10
            }))
          },
          {
            name: 'Pressure',
            value: 3.5,
            unit: 'bar',
            normalRange: { min: 3.0, max: 4.0 },
            criticalRange: { min: 2.5, max: 4.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 3.5 + Math.random() * 0.5 - 0.25
            }))
          },
          {
            name: 'Motor Temperature',
            value: 45,
            unit: '°C',
            normalRange: { min: 35, max: 55 },
            criticalRange: { min: 30, max: 60 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 45 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Power Consumption',
            value: 75,
            unit: 'kW',
            normalRange: { min: 65, max: 85 },
            criticalRange: { min: 60, max: 90 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 75 + Math.random() * 6 - 3
            }))
          },
          {
            name: 'Vibration',
            value: 2.5,
            unit: 'mm/s',
            normalRange: { min: 0, max: 4.5 },
            criticalRange: { min: 0, max: 6.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.5 + Math.random() * 0.4 - 0.2
            }))
          }
        ]
      },
      {
        id: 'valve1',
        name: 'Main Control Valve',
        type: 'Butterfly Valve',
        description: 'Primary flow control valve with electric actuator',
        manufacturer: 'Keystone',
        model: 'F79U-012',
        installationDate: '2023-06-15',
        maintenanceSchedule: 'Quarterly',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 248,
            unit: 'm³/h',
            normalRange: { min: 200, max: 300 },
            criticalRange: { min: 150, max: 350 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 248 + Math.random() * 20 - 10
            }))
          },
          {
            name: 'Position',
            value: 80,
            unit: '%',
            normalRange: { min: 20, max: 100 },
            criticalRange: { min: 10, max: 100 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 80 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Pressure Drop',
            value: 0.5,
            unit: 'bar',
            normalRange: { min: 0.2, max: 0.8 },
            criticalRange: { min: 0.1, max: 1.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.5 + Math.random() * 0.1 - 0.05
            }))
          },
          {
            name: 'Actuator Torque',
            value: 65,
            unit: 'Nm',
            normalRange: { min: 50, max: 80 },
            criticalRange: { min: 40, max: 90 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 65 + Math.random() * 6 - 3
            }))
          }
        ]
      },
      {
        id: 'tank1',
        name: 'Raw Water Basin',
        type: 'Storage Tank',
        description: 'Primary raw water storage and settling basin',
        manufacturer: 'Custom Built',
        installationDate: '2023-04-10',
        maintenanceSchedule: 'Semi-annual',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 245,
            unit: 'm³/h',
            normalRange: { min: 200, max: 300 },
            criticalRange: { min: 150, max: 350 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 245 + Math.random() * 20 - 10
            }))
          },
          {
            name: 'Level',
            value: 85,
            unit: '%',
            normalRange: { min: 40, max: 90 },
            criticalRange: { min: 20, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 85 + Math.random() * 6 - 3
            }))
          },
          {
            name: 'Temperature',
            value: 22.5,
            unit: '°C',
            normalRange: { min: 15, max: 25 },
            criticalRange: { min: 10, max: 30 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 22.5 + Math.random() * 1 - 0.5
            }))
          },
          {
            name: 'Turbidity',
            value: 2.8,
            unit: 'NTU',
            normalRange: { min: 0, max: 5 },
            criticalRange: { min: 0, max: 10 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.8 + Math.random() * 0.4 - 0.2
            }))
          },
          {
            name: 'pH',
            value: 7.2,
            unit: 'pH',
            normalRange: { min: 6.5, max: 8.5 },
            criticalRange: { min: 6.0, max: 9.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 7.2 + Math.random() * 0.2 - 0.1
            }))
          }
        ]
      }
    ];

    setAssets(initialAssets);

    const interval = setInterval(() => {
      setAssets(prevAssets => prevAssets.map(asset => ({
        ...asset,
        metrics: asset.metrics.map(metric => {
          const newValue = metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02);
          return {
            ...metric,
            value: newValue,
            history: [
              ...metric.history.slice(1),
              {
                timestamp: Date.now(),
                value: newValue
              }
            ]
          };
        })
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Rest of the component remains the same
  const getAssetIcon = (assetId: string) => {
    if (assetId.includes('pump')) return <Box className="h-6 w-6" />;
    if (assetId.includes('screen')) return <Filter className="h-6 w-6" />;
    if (assetId.includes('tank')) return <Box className="h-6 w-6" />;
    if (assetId.includes('valve')) return <Gauge className="h-6 w-6" />;
    if (assetId.includes('intake')) return <Droplets className="h-6 w-6" />;
    return <Activity className="h-6 w-6" />;
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Temperature')) return <Thermometer className="h-4 w-4" />;
    if (metricName.includes('Flow')) return <Waves className="h-4 w-4" />;
    if (metricName.includes('Time')) return <Timer className="h-4 w-4" />;
    if (metricName.includes('Power')) return <Zap className="h-4 w-4" />;
    if (metricName.includes('Level')) return <Activity className="h-4 w-4" />;
    if (metricName.includes('Pressure')) return <Gauge className="h-4 w-4" />;
    if (metricName.includes('Vibration')) return <Activity className="h-4 w-4" />;
    if (metricName.includes('Current')) return <Zap className="h-4 w-4" />;
    if (metricName.includes('Torque')) return <RotateCw className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  const isMetricInRange = (value: number, metric: Asset['metrics'][0]) => {
    if (!metric.normalRange) return true;
    return value >= metric.normalRange.min && value <= metric.normalRange.max;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-blue-600 text-white flex items-center justify-between hover:bg-blue-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Droplets className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Stage 1 - Raw Water Stage</h2>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          {/* Process Overview */}
          <div className="mb-6">
            <ProcessFlowDiagram assets={assets} />
          </div>

          {/* Key Metrics Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {assets.map(asset => 
              asset.metrics.slice(0, 1).map(metric => (
                <div key={`${asset.id}-${metric.name}`} className="bg-white rounded-lg shadow p-4">
                  <AssetGauge
                    value={metric.value}
                    min={metric.normalRange?.min || 0}
                    max={metric.normalRange?.max || 100}
                    unit={metric.unit}
                    title={`${asset.name} - ${metric.name}`}
                  />
                </div>
              ))
            )}
          </div>

          {/* Asset Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <div
                key={asset.id}
                onClick={() => setSelectedAsset(asset.id === selectedAsset ? null : asset.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  asset.id === selectedAsset
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getAssetIcon(asset.id)}
                    <div>
                      <h3 className="font-medium">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {asset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between items-baseline">
                      <div className="flex items-center space-x-2">
                        {getMetricIcon(metric.name)}
                        <span className="text-sm text-gray-500">{metric.name}</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        isMetricInRange(metric.value, metric) 
                          ? 'text-gray-900' 
                          : 'text-red-600'
                      }`}>
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};